import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SITE, SERVICE_OPTIONS } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD = "w-full bg-transparent border-b border-white/20 py-4 text-lg text-white placeholder:text-white/30 focus:border-acid focus:outline-none transition-colors";
const LABEL = "font-mono text-[10px] uppercase tracking-[0.25em] text-white/45 mb-1 block";

const initial = { name: "", company: "", email: "", phone: "", service: SERVICE_OPTIONS[0], facility_size: "", message: "" };

export const Booking = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Name, email and phone are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setDone(true);
      toast.success("Request received — we'll be in touch within one business day.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" data-testid="booking-section" className="border-b border-line bg-ink-800">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-acid mb-5">/ 04 — Start here</p>
            <h2 className="font-display font-black tracking-tighter leading-[0.88] text-5xl md:text-7xl">
              GET A<br />QUOTE
            </h2>
            <p className="mt-6 text-white/55 max-w-sm leading-relaxed">
              Tell us about your facility. We'll respond within one business day with a walk-through and a fixed scope.
            </p>
            <div className="mt-10 space-y-3 font-mono text-xs uppercase tracking-[0.15em] text-white/60">
              <p><span className="text-white/30">Call</span> &nbsp;{SITE.phone}</p>
              <p><span className="text-white/30">Call</span> &nbsp;{SITE.phone2}</p>
              <p><span className="text-white/30">Email</span> &nbsp;{SITE.email}</p>
              <p><span className="text-white/30">Area</span> &nbsp;{SITE.serviceArea}</p>
            </div>
          </div>

          <div className="lg:col-span-8 lg:border-l lg:border-line lg:pl-12">
            {done ? (
              <motion.div
                data-testid="quote-success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="h-full min-h-[300px] flex flex-col items-start justify-center"
              >
                <div className="grid place-items-center h-16 w-16 bg-acid text-ink-900 mb-6">
                  <Check size={32} strokeWidth={3} />
                </div>
                <h3 className="font-display font-black text-4xl md:text-5xl tracking-tighter">REQUEST SENT.</h3>
                <p className="mt-4 text-white/55 max-w-md">
                  Thanks — your request is in our queue. An Insight Cleaning Co. account manager will reach out within one business day.
                </p>
                <button
                  data-testid="quote-reset"
                  onClick={() => setDone(false)}
                  className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] border border-line px-6 py-3 hover:bg-acid hover:text-ink-900 hover:border-acid transition-colors"
                >
                  Send another
                </button>
              </motion.div>
            ) : (
              <form data-testid="quote-form" onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                <div>
                  <label className={LABEL}>Full name *</label>
                  <input data-testid="quote-name" className={FIELD} placeholder="Jane Doe" value={form.name} onChange={set("name")} />
                </div>
                <div>
                  <label className={LABEL}>Company</label>
                  <input data-testid="quote-company" className={FIELD} placeholder="Acme Corp" value={form.company} onChange={set("company")} />
                </div>
                <div>
                  <label className={LABEL}>Email *</label>
                  <input data-testid="quote-email" type="email" className={FIELD} placeholder="jane@acme.com" value={form.email} onChange={set("email")} />
                </div>
                <div>
                  <label className={LABEL}>Phone *</label>
                  <input data-testid="quote-phone" className={FIELD} placeholder="(000) 000-0000" value={form.phone} onChange={set("phone")} />
                </div>
                <div>
                  <label className={LABEL}>Service needed</label>
                  <select data-testid="quote-service" className={`${FIELD} appearance-none cursor-pointer`} value={form.service} onChange={set("service")}>
                    {SERVICE_OPTIONS.map((o) => (
                      <option key={o} value={o} className="bg-ink-800 text-white">{o}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={LABEL}>Facility size (sq ft)</label>
                  <input data-testid="quote-size" className={FIELD} placeholder="e.g. 25,000" value={form.facility_size} onChange={set("facility_size")} />
                </div>
                <div className="md:col-span-2">
                  <label className={LABEL}>Anything else?</label>
                  <textarea data-testid="quote-message" rows={3} className={`${FIELD} resize-none`} placeholder="Frequency, current pain points, timeline…" value={form.message} onChange={set("message")} />
                </div>
                <div className="md:col-span-2 pt-2">
                  <button
                    data-testid="quote-submit"
                    type="submit"
                    disabled={loading}
                    className="group w-full flex items-center justify-between bg-acid text-ink-900 px-8 py-6 font-display font-black tracking-tight text-2xl md:text-3xl hover:bg-acid-hover transition-colors disabled:opacity-60"
                  >
                    {loading ? "SENDING…" : "SEND REQUEST"}
                    {loading ? <Loader2 className="animate-spin" size={28} /> : (
                      <ArrowRight size={28} className="group-hover:translate-x-2 transition-transform" />
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
